package org.vreo.devicehub.features.userAuthentication.data.repository

import org.vreo.devicehub.features.core.data.appLogger.AppLogger
import org.vreo.devicehub.features.core.domain.DataError
import org.vreo.devicehub.features.core.domain.Result
import org.vreo.devicehub.features.userAuthentication.data.dto.UserSignInRequestDto
import org.vreo.devicehub.features.userAuthentication.data.mappers.toUser
import org.vreo.devicehub.features.userAuthentication.data.network.KtorRemoteUserDataSource
import org.vreo.devicehub.features.userAuthentication.domain.User
import org.vreo.devicehub.features.userAuthentication.data.mappers.toUserDetailsDto
import org.vreo.devicehub.features.userAuthentication.domain.UserAuthenticationRepository
import org.vreo.devicehub.features.userAuthentication.data.dto.NewVerificationCodeRequest
import org.vreo.devicehub.features.userAuthentication.data.dto.VerificationDto

/**
 * User authentication repository implementation.
 *
 * @param userDataSource remote data source for user authentication.
 */
class UserAuthenticationRepositoryImpl(
    private val userDataSource: KtorRemoteUserDataSource
) : UserAuthenticationRepository {
    /**
     * Signs in user.
     *
     * @param username user login username, email or mobile number
     * @param password user password
     * @return Result of JWT token on success or error on failure.
     */
    override suspend fun signIn(
        username: String,
        password: String,
    ): Result<String, DataError.Remote> {
        return try {
            val userCredentials = UserSignInRequestDto(
                username = username,
                password = password
            )

            when (val result = userDataSource.signIn(userCredentials)) {
                is Result.Success -> {
                    Result.Success(result.data.token)
                }

                is Result.Error -> {
                    Result.Error(result.error)
                }
            }
        } catch (e: Exception) {
            AppLogger.error("Failed to sign in user, error: ${e.message}")
            return Result.Error(DataError.Remote.UNKNOWN)
        }
    }

    /**
     * Signs up (register) new user.
     *
     * @param user user to be registered
     * @return Result of registration verification valid until on success or error on failure.
     */
    override suspend fun signUp(
        user: User
    ): Result<Long, DataError.Remote> {
        return try {
            when (val result = userDataSource.registerNewUser(
                user.toUserDetailsDto()
            )) {
                is Result.Success -> {
                    Result.Success(result.data.validationCodeValidUntil)
                }

                is Result.Error -> {
                    Result.Error(result.error)
                }
            }
        } catch (e: Exception) {
            AppLogger.error("Failed to sign up new user, error: ${e.message}")
            Result.Error(DataError.Remote.UNKNOWN)
        }
    }

    /**
     * Request new verification code for pending user
     *
     * @param username email address for which new verification code is requested
     * @return Result of user registration response on success or error on failure
     */
    override suspend fun requestNewVerificationCode(
        username: String
    ): Result<Long, DataError.Remote> {
        return try {
            when (val result = userDataSource.requestNewVerificationCode(
                NewVerificationCodeRequest(email = username)
            )) {
                is Result.Success -> {
                    Result.Success(result.data.validationCodeValidUntil)
                }

                is Result.Error -> {
                    Result.Error(result.error)
                }
            }
        } catch (e: Exception) {
            AppLogger.error("Failed to request new verification code, error: ${e.message}")
            Result.Error(DataError.Remote.UNKNOWN)
        }
    }

    /**
     * Verify user account
     *
     * @param username username of the account to be verified
     * @param verificationCode verification code to be used for verification
     * @return Result of JWT token on success or error on failure
     */
    override suspend fun verifyUser(
        username: String,
        verificationCode: String,
    ): Result<String, DataError.Remote> {
        return try {
            when (val result = userDataSource.verifyUser(
                VerificationDto(email = username, code = verificationCode)
            )) {
                is Result.Success -> {
                    Result.Success(result.data.token)
                }

                is Result.Error -> {
                    Result.Error(result.error)
                }
            }
        } catch (e: Exception) {
            AppLogger.error("Failed to sign in user, error: ${e.message}")
            return Result.Error(DataError.Remote.UNKNOWN)
        }
    }

    /**
     * Get user details using authentication token
     *
     * @param token JWT authentication token for authenticated user
     * @return Result of user details on success or error on failure
     */
    override suspend fun getUserDetails(
        token: String
    ): Result<User, DataError.Remote> {
        return try {
            when (val result = userDataSource.getUserDetails(token)) {
                is Result.Success -> {
                    Result.Success(result.data.toUser())
                }

                is Result.Error -> {
                    Result.Error(result.error)
                }
            }
        } catch (e: Exception) {
            AppLogger.error("Failed to sign in user, error: ${e.message}")
            Result.Error(DataError.Remote.UNKNOWN)
        }
    }
}